import { action, makeObservable, observable } from "mobx";
import dayjs from "dayjs";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import errorToast from "../../components/toast/ErrorToast";
import SaporeForm from "../../forms/SaporeForm";
import DateField from "../../forms/fields/DateField";
import MultipleOptionsField from "../../forms/fields/MultipleOptionField";
import BooleanField from "../../forms/fields/BooleanField";
import DashboardDateRangeValidity from "./DashboardDateRangeValidity";


export const MONEY = "money";
export const BOXES = "boxes";

export default class DashboardFeature {

    constructor(application, navigator, unitAwareChartKeys = []) {
        this._application = application;
        this._navigate = navigator;
        this._unitAwareChartKeys = unitAwareChartKeys;
        this._filtersForm = this._createFiltersForm();

        this._chartUnits = Object.fromEntries(this._unitAwareChartKeys.map((chartKey) => [chartKey, MONEY]));
        this._salesByDestinationLoaded = false;
        this._salesByDestination = [];
        this._channelOptions = [];
        this._channelOptionsLoaded = false;
        this._articleCategoryOptions = [];
        this._articleCategoryOptionsLoaded = false;
        this._zoomedChannel = null;
        this._channelsBeforeZoom = null;
        this._zoomedCategory = null;
        this._categoriesBeforeZoom = null;

        makeObservable(this, {
            _chartUnits: observable,
            setChartUnit: action,
            setAllUnits: action,
            _channelOptions: observable,
            setChannelOptions: action,
            _articleCategoryOptions: observable,
            setArticleCategoryOptions: action,
            _salesByDestinationLoaded: observable,
            _salesByDestination: observable,
            setSalesByDestination: action,
            _resetSections: action,
            _zoomedChannel: observable,
            _channelsBeforeZoom: observable,
            zoomIntoChannel: action,
            exitChannelZoom: action,
            _dropZoomKeepingManualSelection: action,
            _zoomedCategory: observable,
            _categoriesBeforeZoom: observable,
            zoomIntoCategory: action,
            exitCategoryZoom: action,
            _dropCategoryZoomKeepingManualSelection: action,
        });
    }

    zoomIntoChannel(channel) {
        this._channelsBeforeZoom = [...this.channelsField().value];
        this._zoomedChannel = channel;
        this.channelsField().updateValue([channel]);
    }

    exitChannelZoom() {
        this._zoomedChannel = null;
        const restoredSelection = this._channelsBeforeZoom ?? [];
        this._channelsBeforeZoom = null;
        this.channelsField().updateValue(restoredSelection);
    }

    _dropZoomKeepingManualSelection() {
        this._zoomedChannel = null;
        this._channelsBeforeZoom = null;
    }

    _userChangedChannelsWhileZoomed() {
        return this.zoomedChannel() !== null && !this._channelsFieldHoldsOnly(this.zoomedChannel());
    }

    _channelsFieldHoldsOnly(channel) {
        const selectedChannels = this.channelsField().value;
        return selectedChannels.length === 1 && selectedChannels[0] === channel;
    }

    zoomedChannel() {
        return this._zoomedChannel;
    }

    zoomIntoCategory(category) {
        this._categoriesBeforeZoom = [...this.articleCategoriesField().value];
        this._zoomedCategory = category;
        this.articleCategoriesField().updateValue([category]);
    }

    exitCategoryZoom() {
        this._zoomedCategory = null;
        const restoredSelection = this._categoriesBeforeZoom ?? [];
        this._categoriesBeforeZoom = null;
        this.articleCategoriesField().updateValue(restoredSelection);
    }

    _dropCategoryZoomKeepingManualSelection() {
        this._zoomedCategory = null;
        this._categoriesBeforeZoom = null;
    }

    _userChangedCategoriesWhileZoomed() {
        return this.zoomedCategory() !== null && !this._categoriesFieldHoldsOnly(this.zoomedCategory());
    }

    _categoriesFieldHoldsOnly(category) {
        const selectedCategories = this.articleCategoriesField().value;
        return selectedCategories.length === 1 && selectedCategories[0] === category;
    }

    zoomedCategory() {
        return this._zoomedCategory;
    }

    units() {
        return [
            { value: MONEY, label: "Facturación", Icon: PaidOutlinedIcon },
            { value: BOXES, label: "Cajas", Icon: Inventory2OutlinedIcon },
        ];
    }

    allowanceModes() {
        return [
            { value: false, label: "Con bonificaciones", Icon: LocalOfferOutlinedIcon },
            { value: true, label: "Sin bonificaciones", Icon: MoneyOffOutlinedIcon },
        ];
    }

    unitFor(chartKey) {
        return this._chartUnits[chartKey];
    }

    isBoxes(chartKey) {
        return this.unitFor(chartKey) === BOXES;
    }

    setChartUnit(chartKey, unit) {
        this._chartUnits[chartKey] = unit;
    }

    setAllUnits(unit) {
        this._unitAwareChartKeys.forEach((chartKey) => {
            this._chartUnits[chartKey] = unit;
        });
    }

    globalUnit() {
        const units = this._unitAwareChartKeys.map((chartKey) => this._chartUnits[chartKey]);
        const allShareOneUnit = units.every((unit) => unit === units[0]);
        return allShareOneUnit ? units[0] : null;
    }

    _createFiltersForm() {
        const fields = {
            fromDate: new DateField(false, dayjs().startOf("month"), this),
            toDate: new DateField(false, dayjs().endOf("month"), this),
            channels: new MultipleOptionsField([], true, this, []),
            articleCategories: new MultipleOptionsField([], true, this, []),
            withoutAllowances: new BooleanField(false),
        };
        return new SaporeForm(fields);
    }

    setSalesByDestination(data) {
        this._salesByDestination = data;
        this._salesByDestinationLoaded = true;
    }

    _fetchSalesByDestination() {
        const response = this._application.apiClient().getSalesByDestination();
        this.setSalesByDestination(response.salesByDestination());
    }

    _resetSections() {
        this._salesByDestinationLoaded = false;
        this._salesByDestination = [];
    }

    aValueChanged() {
        if (this._userChangedChannelsWhileZoomed()) {
            this._dropZoomKeepingManualSelection();
        }
        if (this._userChangedCategoriesWhileZoomed()) {
            this._dropCategoryZoomKeepingManualSelection();
        }
        this.load();
    }

    _loadChannelOptionsOnce() {
        if (this._channelOptionsLoaded) {
            return;
        }
        this._channelOptionsLoaded = true;
        this._loadChannelOptions();
    }

    async _loadChannelOptions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.setChannelOptions(response.channels().map((channel) => ({ label: channel, value: channel })));
            },
            handlesError: () => {
                errorToast("No se pudieron obtener los tipos de cliente");
            },
        });
        return this._application.apiClient().getDashboardChannels(responseHandler);
    }

    setChannelOptions(options) {
        this._channelOptions = options;
        this.channelsField().updateOptions(options);
    }

    _loadArticleCategoryOptionsOnce() {
        if (this._articleCategoryOptionsLoaded) {
            return;
        }
        this._articleCategoryOptionsLoaded = true;
        this._loadArticleCategoryOptions();
    }

    async _loadArticleCategoryOptions() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.setArticleCategoryOptions(response.articleCategories().map(
                    (articleCategory) => ({ label: articleCategory.title, value: articleCategory.name })));
            },
            handlesError: () => {
                errorToast("No se pudieron obtener las categorías de artículos");
            },
        });
        return this._application.apiClient().getDashboardArticleCategories(responseHandler);
    }

    setArticleCategoryOptions(options) {
        this._articleCategoryOptions = options;
        this.articleCategoriesField().updateOptions(options);
    }

    async load() {
        this._loadChannelOptionsOnce();
        this._loadArticleCategoryOptionsOnce();
        const fromDate = this.fromDateField().value;
        const toDate = this.toDateField().value;
        if (!fromDate?.isValid?.() || !toDate?.isValid?.()) {
            return;
        }
        if (!DashboardDateRangeValidity.isValid(fromDate, toDate)) {
            this.toDateField().setErrors(["Debe ser igual o posterior a Desde"]);
            return;
        }
        this.toDateField().setErrors([]);
        this._resetSections();
        this._fetchSalesByDestination();
    }

    get loaded() {
        return true;
    }

    filtersForm() {
        return this._filtersForm;
    }

    fromDateField() {
        return this.filtersForm().getFieldByName("fromDate");
    }

    toDateField() {
        return this.filtersForm().getFieldByName("toDate");
    }

    channelsField() {
        return this.filtersForm().getFieldByName("channels");
    }

    articleCategoriesField() {
        return this.filtersForm().getFieldByName("articleCategories");
    }

    withoutAllowancesField() {
        return this.filtersForm().getFieldByName("withoutAllowances");
    }

    channelOptions() {
        return this._channelOptions;
    }

    articleCategoryOptions() {
        return this._articleCategoryOptions;
    }

    get salesByDestinationLoaded() {
        return this._salesByDestinationLoaded;
    }

    get salesByDestination() {
        return this._salesByDestination;
    }
}
